/**
 * Parser del Excel/CSV del cliente. Puro (sin React) y testeado con fixtures.
 * Entrada: una matriz de celdas (strings) por hoja. Salida: filas listas para importar_catalogo.
 */

export type Columna = "sku" | "nombre" | "unidad" | "existencia" | "minimo" | "ubicacion" | "consumo" | "almacen";
export type Mapeo = Partial<Record<Columna, number>>; // columna → índice
export type FilaImportar = { fila: number; sku: string; nombre: string | null; unidad: string | null; existencia: number | null; minimo: number | null; ubicacion: string | null; consumo: number | null; periodo: 30 | 90 | null };
export type Rechazo = { fila: number; sku: string | null; motivo: string };

export const ALIAS: Record<Columna, string[]> = {
  sku: ["sku", "clave", "codigo", "código", "cod", "articulo", "artículo", "id", "referencia", "item", "clave producto", "clave articulo"],
  nombre: ["nombre", "descripcion", "descripción", "producto", "articulo descripcion", "concepto", "detalle"],
  unidad: ["unidad", "um", "u.m.", "u.m", "u/m", "medida", "unidad de medida"],
  existencia: ["existencia", "existencias", "stock", "cantidad", "inventario", "exist", "disponible", "saldo", "fisico", "físico"],
  minimo: ["minimo", "mínimo", "min", "stock minimo", "stock mínimo", "reorden", "punto de reorden", "min stock"],
  ubicacion: ["ubicacion", "ubicación", "localizacion", "localización", "posicion", "posición", "rack", "anaquel", "zona"],
  consumo: ["consumo", "rotacion", "rotación", "consumo mensual", "rotacion mensual", "rotación mensual", "consumo 3 meses", "rotacion 3 meses", "rotación 3 meses", "consumo trimestral", "rotacion trimestral", "venta mensual", "salidas mes"],
  almacen: ["almacen", "almacén", "bodega", "sucursal", "tienda"],
};

export function normalizarCabecera(s: unknown): string {
  return String(s ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9./ ]/g, " ").replace(/\s+/g, " ").trim();
}

/** Encuentra la fila de cabecera: la primera con ≥ 2 alias conocidos (los ERP ponen títulos arriba). */
export function detectarCabecera(hoja: unknown[][], maxFilas = 15): number {
  for (let i = 0; i < Math.min(maxFilas, hoja.length); i++) {
    const fila = hoja[i] ?? [];
    const conocidas = fila.filter((c) => reconocerColumna(c) !== null).length;
    if (conocidas >= 2) return i;
  }
  return 0;
}

export function reconocerColumna(cabecera: unknown): Columna | null {
  const n = normalizarCabecera(cabecera);
  if (!n) return null;
  for (const [col, alias] of Object.entries(ALIAS) as [Columna, string[]][]) {
    if (alias.some((a) => normalizarCabecera(a) === n)) return col;
  }
  // coincidencia parcial (p. ej. "existencia actual", "rotacion 3 meses (pzas)")
  for (const [col, alias] of Object.entries(ALIAS) as [Columna, string[]][]) {
    if (alias.some((a) => n.startsWith(normalizarCabecera(a) + " ") || n.includes(" " + normalizarCabecera(a)))) return col;
  }
  return null;
}

export function mapeoAutomatico(cabeceras: unknown[]): Mapeo {
  const m: Mapeo = {};
  cabeceras.forEach((c, i) => {
    const col = reconocerColumna(c);
    if (col && m[col] === undefined) m[col] = i;
  });
  return m;
}

/** Deduce el periodo de rotación desde el nombre de la columna: "3 meses"/"trimestral" → 90, si no → 30. */
export function periodoDesdeCabecera(cabecera: unknown): 30 | 90 {
  const n = normalizarCabecera(cabecera);
  return /3 meses|trimestr|90/.test(n) ? 90 : 30;
}

/**
 * Regla numérica POR COLUMNA: si algún valor de la columna trae ".", la "," es separador de
 * miles en toda la columna (1,250 → 1250). Si ninguno trae ".", la "," es decimal (12,5 → 12.5).
 */
export function detectarComaDecimal(valores: unknown[]): boolean {
  const textos = valores.map((v) => String(v ?? "").trim()).filter((v) => /\d/.test(v));
  const conPunto = textos.some((t) => t.includes("."));
  return !conPunto;
}

export function parsearNumero(v: unknown, comaDecimal: boolean): number | null | "invalido" {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : "invalido";
  let s = String(v).trim();
  if (s === "") return null;
  s = s.replace(/[^\d.,\-]/g, ""); // quita unidades, espacios, $ etc.
  if (s === "" || s === "-") return "invalido";
  if (comaDecimal) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/,/g, "");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : "invalido";
}

export type ResultadoParseo = {
  filas: FilaImportar[];
  rechazadas: Rechazo[];
  avisos: string[];
  periodo: 30 | 90;
  columnasDetectadas: Mapeo;
};

export function parsearHoja(hoja: unknown[][], mapeo: Mapeo, filaCabecera: number, opciones: { periodo?: 30 | 90 } = {}): ResultadoParseo {
  const filas: FilaImportar[] = [];
  const rechazadas: Rechazo[] = [];
  const avisos: string[] = [];
  const datos = hoja.slice(filaCabecera + 1);
  const cabeceras = hoja[filaCabecera] ?? [];
  const periodo = opciones.periodo ?? (mapeo.consumo !== undefined ? periodoDesdeCabecera(cabeceras[mapeo.consumo]) : 30);

  const col = (fila: unknown[], c: Columna): unknown => (mapeo[c] === undefined ? undefined : fila[mapeo[c]!]);
  const comaDecimalExist = mapeo.existencia !== undefined ? detectarComaDecimal(datos.map((f) => col(f, "existencia"))) : true;
  const comaDecimalMin = mapeo.minimo !== undefined ? detectarComaDecimal(datos.map((f) => col(f, "minimo"))) : true;
  const comaDecimalCons = mapeo.consumo !== undefined ? detectarComaDecimal(datos.map((f) => col(f, "consumo"))) : true;

  let vacias = 0, avisosTresDecimales = 0;
  datos.forEach((fila, i) => {
    const numFila = filaCabecera + i + 2; // 1-based como en Excel
    const vacia = (fila ?? []).every((c) => c === null || c === undefined || String(c).trim() === "");
    if (vacia) { vacias++; return; }
    const skuCrudo = String(col(fila, "sku") ?? "").trim();
    const nombre = String(col(fila, "nombre") ?? "").trim() || null;
    // fila de totales / basura: sin sku y con texto tipo "TOTAL"
    if (!skuCrudo && /total|suma|subtotal/i.test(String((fila ?? []).join(" ")))) return;
    if (!skuCrudo) { rechazadas.push({ fila: numFila, sku: null, motivo: "SIN_CLAVE" }); return; }
    const existencia = parsearNumero(col(fila, "existencia"), comaDecimalExist);
    if (existencia === "invalido") { rechazadas.push({ fila: numFila, sku: skuCrudo, motivo: "EXISTENCIA_NO_NUMERICA" }); return; }
    const minimo = parsearNumero(col(fila, "minimo"), comaDecimalMin);
    if (minimo === "invalido") { rechazadas.push({ fila: numFila, sku: skuCrudo, motivo: "MINIMO_NO_NUMERICO" }); return; }
    if (minimo != null && minimo < 0) { rechazadas.push({ fila: numFila, sku: skuCrudo, motivo: "MINIMO_NEGATIVO" }); return; }
    const consumo = parsearNumero(col(fila, "consumo"), comaDecimalCons);
    if (comaDecimalExist && /,\d{3}$/.test(String(col(fila, "existencia") ?? ""))) avisosTresDecimales++;
    filas.push({
      fila: numFila,
      sku: skuCrudo,
      nombre,
      unidad: String(col(fila, "unidad") ?? "").trim().toUpperCase() || null,
      existencia: existencia,   // null = celda vacía: la base conserva el ERP anterior y pone físico 0 si es nuevo
      minimo: minimo ?? null,
      ubicacion: String(col(fila, "ubicacion") ?? "").trim() || null,
      consumo: consumo === "invalido" ? null : consumo,
      periodo: consumo != null && consumo !== "invalido" ? periodo : null,
    });
  });
  if (vacias) avisos.push(`${vacias} fila(s) vacías ignoradas`);
  if (avisosTresDecimales) avisos.push(`${avisosTresDecimales} valor(es) con coma y 3 dígitos se leyeron como decimales (ej. 1,250 → 1.25). Revisa la columna Existencia.`);
  const sinExistencia = filas.filter((f) => col(datos[f.fila - filaCabecera - 2] ?? [], "existencia") === undefined || String(col(datos[f.fila - filaCabecera - 2] ?? [], "existencia") ?? "").trim() === "").length;
  if (sinExistencia) avisos.push(`${sinExistencia} fila(s) sin existencia se importarán con 0`);
  return { filas, rechazadas, avisos, periodo, columnasDetectadas: mapeo };
}

/** Lee un ArrayBuffer de CSV: BOM/UTF-8 → si falla, windows-1252. Devuelve el texto. */
export function decodificarCsv(buf: ArrayBuffer): { texto: string; encoding: string } {
  const bytes = new Uint8Array(buf);
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return { texto: new TextDecoder("utf-8").decode(bytes.subarray(3)), encoding: "utf-8-bom" };
  try {
    return { texto: new TextDecoder("utf-8", { fatal: true }).decode(bytes), encoding: "utf-8" };
  } catch {
    return { texto: new TextDecoder("windows-1252").decode(bytes), encoding: "windows-1252" };
  }
}

export function detectarSeparador(texto: string): "," | ";" | "\t" {
  // Se cuenta sobre las primeras 10 líneas no vacías (la primera suele ser un título con comas).
  const linea = texto.split(/\r?\n/).filter((l) => l.trim() !== "").slice(0, 10).join("\n");
  const cuenta = (c: string) => (linea.match(new RegExp(c === "\t" ? "\t" : `\\${c}`, "g")) ?? []).length;
  const pc = cuenta(";"), tab = cuenta("\t"), coma = cuenta(",");
  if (tab >= pc && tab >= coma && tab > 0) return "\t";
  return pc > coma ? ";" : ",";
}
